import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock, Send, CheckCircle } from 'lucide-react';
import { submitToGoogleForm } from '../lib/googleForm';
import './ContactPage.css';

const fleetSizes  = ['1–5 vehicles', '6–20 vehicles', '21–50 vehicles', '50+ vehicles'];
const fleetTypes  = ['Aluminum Box', 'Cargo Box', 'Temp Controlled (+25°C)', 'Cold Chain (2–8°C)', 'Frozen (−18 to −25°C)', 'Skeleton / Custom', 'Multiple / Not Sure'];
const sources     = ['Google Search', 'LinkedIn', 'Facebook', 'Referral / Word of Mouth', 'Event / Conference', 'Other'];

const info = [
  { icon: Phone, label: 'Call Us',       value: '082-992-4569',  href: 'tel:+66829924569' },
  { icon: Mail,  label: 'Email',         value: 'dev@akkra.co.th', href: 'mailto:dev@akkra.co.th' },
  { icon: Clock, label: 'Response Time', value: 'Within 24 hours', href: null },
  { icon: MapPin, label: 'Headquarters', value: '118/1 Rama VI Road, Phaya Thai Subdistrict, Phaya Thai District, Bangkok 10400, Thailand', href: 'https://maps.google.com/?q=118/1+Rama+VI+Road+Phaya+Thai+Bangkok+10400+Thailand' },
];

export default function ContactPage() {
  const [form, setForm] = useState({
    name: '', company: '', jobTitle: '', email: '',
    phone: '', country: '', fleetSize: '', fleetType: '', message: '', source: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);

  const set = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await submitToGoogleForm(form);
    } catch (err) {
      // no-cors gives an opaque response; network errors are rare. Still show
      // success so the user isn't blocked — the POST almost always lands.
      console.error('[contact] submit error', err);
    }
    setSending(false);
    setSubmitted(true);
  };

  return (
    <div className="contact-page">
      <div className="contact-hero">
        <img src="/axion-logo.png" alt="Axion Mobility" className="contact-logo" />
        <span className="section-label">Get In Touch</span>
        <h1 className="contact-title">Let's Build Your Fleet</h1>
        <p className="contact-subtitle">
          Book a demo, request a quote, or simply ask us anything.
          Our team responds within 24 hours.
        </p>
      </div>

      <div className="contact-body">
        {/* ── Info cards ── */}
        <div className="contact-info">
          {info.map(({ icon: Icon, label, value, href }) => (
            <div key={label} className="contact-info-card">
              <div className="ci-icon"><Icon size={20} /></div>
              <div>
                <p className="ci-label">{label}</p>
                {href
                  ? <a className="ci-value" href={href}>{value}</a>
                  : <p className="ci-value">{value}</p>
                }
              </div>
            </div>
          ))}
        </div>

        {/* ── Form ── */}
        <div className="contact-form-wrap">
          {submitted ? (
            <div className="contact-success">
              <CheckCircle size={56} color="var(--axion-red)" />
              <h2>Thank You!</h2>
              <p>We've received your enquiry and will be in touch within 24 hours.</p>
            </div>
          ) : (
            <form className="contact-form" onSubmit={handleSubmit}>
              <h2 className="form-title">Book a Demo</h2>
              <p className="form-subtitle">5,000 THB deposit — fully refundable on order placement.</p>

              <div className="form-grid">
                <div className="form-field">
                  <label>Full Name <span>*</span></label>
                  <input name="name" required placeholder="John Smith" onChange={set} />
                </div>
                <div className="form-field">
                  <label>Company Name <span>*</span></label>
                  <input name="company" required placeholder="Your Company Ltd." onChange={set} />
                </div>
                <div className="form-field">
                  <label>Job Title</label>
                  <input name="jobTitle" placeholder="Fleet Manager" onChange={set} />
                </div>
                <div className="form-field">
                  <label>Email Address <span>*</span></label>
                  <input name="email" type="email" required placeholder="john@company.com" onChange={set} />
                </div>
                <div className="form-field">
                  <label>Phone Number</label>
                  <input name="phone" type="tel" placeholder="+66 xx xxx xxxx" onChange={set} />
                </div>
                <div className="form-field">
                  <label>Country / Location</label>
                  <input name="country" placeholder="Thailand" onChange={set} />
                </div>
                <div className="form-field">
                  <label>Fleet Size</label>
                  <select name="fleetSize" onChange={set}>
                    <option value="">Select fleet size…</option>
                    {fleetSizes.map(s => <option key={s}>{s}</option>)}
                  </select>
                </div>
                <div className="form-field">
                  <label>Vehicle Type of Interest</label>
                  <select name="fleetType" onChange={set}>
                    <option value="">Select vehicle type…</option>
                    {fleetTypes.map(t => <option key={t}>{t}</option>)}
                  </select>
                </div>
              </div>

              <div className="form-field form-field--full">
                <label>Message</label>
                <textarea name="message" rows={4} placeholder="Tell us about your delivery needs, current fleet situation, or any questions you have…" onChange={set} />
              </div>

              <div className="form-field form-field--full">
                <label>How did you hear about us?</label>
                <select name="source" onChange={set}>
                  <option value="">Select…</option>
                  {sources.map(s => <option key={s}>{s}</option>)}
                </select>
              </div>

              <button type="submit" className="form-submit" disabled={sending}>
                <Send size={16} />
                {sending ? 'Sending…' : 'Submit Enquiry'}
              </button>
              <p className="form-note">
                By submitting you agree to our <Link to="/about">Privacy Policy</Link>.
                The 5,000 THB demo deposit is collected separately after confirmation.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
